<template>
  <div class="containers">
    <Header
      :title="headerTitle | titleCase"
      hide-action
      :track-event-context="CMP_CONTEXTS.MY_REQUEST_DETAIL"
      :ticket-id="ticket.id"
      :back-callback="handleBack"
    />

    <div class="title">
      {{ ticket.subject }}
    </div>
    <div
      ref="container"
      v-loading.center="{ isLoading: loading }"
      class="message-container"
      :class="{ closed: !needsActionButton }"
      @scroll.passive="isScrollable"
    >
      <div class="detail">
        <div class="detail__left">
          <p>{{ $t('page.my_request.col.created') }}</p>
          <p>{{ $t('page.my_request.col.last_activity') }}</p>
          <p>ID</p>
          <p>{{ $t('page.my_request.col.status') }}</p>
        </div>
        <div class="detail__right">
          <p>{{ ticket.createdAt | formatDate }}</p>
          <p>{{ ticket.updatedAt | formatDate }}</p>
          <p>#{{ ticket.id }}</p>
          <div class="status">
            <div :class="isActive ? 'active' : 'done'">
              {{ ticketStatus | titleCase }}
            </div>
          </div>
        </div>
      </div>
      <div v-for="comment in commentList" :key="comment.id" class="message">
        <div v-if="comment.authorId === ticket.userId" class="avatar">
          <img class="avatar-user" :src="require('@/assets/img/user_avatar.png')" />
        </div>
        <div v-else class="message__avatarAgent">
          <img :src="require('@/assets/img/shared/default/my_requests_avatar.png')" />
        </div>
        <div class="message__body">
          <div class="name">
            {{ comment.author.name }}
          </div>
          <div class="reply">
            {{ comment.body }}
            <div v-if="comment.attachments.length" class="attachment">
              <div class="attachment__title">
                {{ $t('page.my_request.single.attachments') }}
              </div>
              <template v-for="attachment in comment.attachments">
                <span
                  v-if="isAttachmentImage(attachment)"
                  :key="attachment.id"
                  class="attachment__link"
                  @click="handleShowAttachment(attachment)"
                >
                  {{ attachment.fileName }}
                </span>
                <a
                  v-else
                  :key="attachment.id"
                  class="attachment__link"
                  :style="{ color: 'black' }"
                  :href="attachment.url"
                  :download="attachment.fileName"
                  >⬇️ &nbsp;{{ attachment.fileName }}</a
                >
              </template>
            </div>
          </div>
        </div>
        <div class="message__time">
          {{ comment.createdAt | formatTime }}
        </div>
      </div>
      <div v-if="scrollable" class="scroll">
        <img v-if="!scrolledDown" :src="require('@/assets/img/arrow_down.png')" @click="scrollToEnd" />
        <img v-else :src="require('@/assets/img/arrow_up.png')" @click="scrollToTop" />
      </div>
    </div>
    <ImageView
      v-if="showAttachment"
      class="attachment-view"
      :title="selectedAttachment.title"
      :image-url="selectedAttachment.url"
      @close="showAttachment = false"
    />
    <!-- <ChatRating
      v-if="showRating"
      mode="ticket"
      :entity-id="ticketId"
      :feedback-categories="feedbackCategories"
      :track-event-context=“CMP_CONTEXTS.MY_REQUEST_DETAIL”
      @send:feedback="postFeedback"
      @submit="handleFeedback"
    /> -->
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import Header from '@/components/Header.vue';
import ImageView from './ImageView.vue';
import Input from './Input.vue';
import ChatRating from '@/views/Chatroom/ChatRating.vue';

import * as PleasedApi from '@/common/api/api.pleased';
import * as PleasedModel from '@/common/api/models.pleased';
import loading from '@/components/loading';
import { UserTicket } from '../../common/api/model';
import { CMP_CONTEXTS } from '@/common/logs/events';
import LogPageEventMixin, { PAGE_TRACKING, PAGE_EVENTS } from '@/mixins/logPageEvent';
import { mixins } from 'vue-class-component';

@Component({
  components: {
    Header,
    Input,
    ImageView,
    ChatRating,
  },
  filters: {
    formatDate(requestTime: string) {
      const options = { month: 'long', year: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' } as const;
      const requestCreated = new Date(requestTime);
      return requestCreated.toLocaleString(undefined, options);
    },
    formatTime(requestTime: string) {
      const options = { hour: 'numeric', minute: 'numeric' } as const;
      const requestCreated = new Date(requestTime);
      return requestCreated.toLocaleString(undefined, options);
    },
    capitalize(word: string) {
      if (word) {
        return word[0].toUpperCase() + word.substring(1);
      }
    },
    titleCase(str: string) {
      return str.replace(/\w\S*/g, function (txt) {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
      });
    },
  },
  directives: {
    loading,
  },
})
export default class MyRequestDetail extends mixins(LogPageEventMixin) {
  @Prop({ default: {} }) readonly ticketId!: string;
  @Prop({ default: {} }) readonly initialTicket!: UserTicket;

  readonly CMP_CONTEXTS = CMP_CONTEXTS;

  ticket: any = this.initialTicket;
  ticketReviews: PleasedModel.EntityReview[] = [];
  commentList: PleasedModel.Comment[] = [];
  addCommentSelect: boolean = false;
  scrollable: boolean = false;
  scrolledDown: boolean = true;
  showAttachment: boolean = false;
  selectedAttachment = {} as { title: string; url: string };
  uploading: boolean = false;
  loading: boolean = false;
  // TODO: uncomment or remove commented code for showing chat rating here
  // loadingReviews: boolean = false;
  // feedbackCategories: PleasedModel.FeedbackCategory[] = [];
  submittedFeedback: boolean = false;

  mounted() {
    this.loading = true;
    // this.loadingReviews = true;
    this.getDetails().then(() => {
      this.loading = false;
    });
    this.getCommentList();
    this.isScrollable();

    // this.getReviews().then(() => {
    //   this.loadingReviews = false;
    // });

    // PleasedApi.getFeedbackSelections().then(r => {
    //   this.feedbackCategories = r;
    // });
  }

  updated() {
    this.isScrollable();
  }

  get [PAGE_TRACKING]() {
    return { name: PAGE_EVENTS.MY_REQUESTS_DETAIL, ticketId: this.ticket.id };
  }

  get needsActionButton() {
    return !this.ticketSolvedOrClosed;
  }
  get ticketStatus() {
    const { status } = this.ticket;
    return this.ticketSolvedOrClosed ? status : 'Active';
  }

  get ticketSolvedOrClosed() {
    return this.ticket && ['solved', 'closed', 'resolved'].includes(this.ticket.status?.toLowerCase());
  }

  get isActive() {
    return this.ticketStatus === 'Active';
  }
  get headerTitle() {
    const { ticketStatus } = this;
    return this.$t('page.my_request.request_details', { ticketStatus });
  }

  // get showRating () {
  //   return !this.submittedFeedback && !this.loadingReviews && this.ticketReviews.length === 0;
  // }

  isScrollable() {
    const container = this.$refs.container as HTMLDivElement;
    if (container.scrollHeight <= container.clientHeight + 100) {
      this.scrollable = false;
    } else {
      if (container.scrollTop + container.clientHeight / 2 < container.scrollHeight / 2) {
        this.scrolledDown = false;
      } else {
        this.scrolledDown = true;
      }
      this.scrollable = true;
    }
  }
  getCommentList() {
    PleasedApi.getTicketComments(parseInt(this.ticketId)).then((res) => {
      this.commentList = res.data;
      this.$nextTick(() => {
        this.scrollToEnd();
      });
      return res.data;
    });
  }
  getDetails() {
    return PleasedApi.getTicket(parseInt(this.ticketId)).then((ticket) => {
      if (ticket.updatedAt !== this.ticket.updatedAt) {
        this.$emit('update-ticket');
      }

      this.ticket = ticket;
    });
  }

  async getReviews() {
    const { reviews } = await PleasedApi.getEntityReview({ entityType: 'ticket', entityId: parseInt(this.ticketId) });
    const ticketReviews = reviews.filter((review) => review.type === 'TICKET');
    this.ticketReviews = ticketReviews;
  }

  handleBack() {
    this.$emit('back');
  }
  addComment() {
    this.addCommentSelect = true;
  }
  submitComment(comment: string) {
    PleasedApi.addTicketComment({ comment, ticketId: this.ticket.id }).then(() => {
      this.getDetails();
      this.getCommentList();
    });
  }
  attachImage(image: File, details: string) {
    this.uploading = true;
    PleasedApi.addAttachmentComment({ file: image, ticketId: this.ticket.id })
      .then(() => this.getCommentList())
      .finally(() => {
        this.uploading = false;
        return this.getDetails();
      });
  }
  handleShowAttachment(attachment: PleasedModel.TicketAttachment) {
    const { fileName, url } = attachment;
    this.selectedAttachment.title = fileName;
    this.selectedAttachment.url = url;
    this.showAttachment = true;
  }
  isAttachmentImage(attachment: PleasedModel.TicketAttachment) {
    return /^image/.test(attachment.contentType);
  }

  // postFeedback (config: {rating: number, comment: string, feedbackSelectionIds: number[]; type: 'voice' | 'chat'}) {
  //   const { rating, comment, feedbackSelectionIds, type } = config;

  //   PleasedApi.postFeedback({
  //     rating,
  //     comment,
  //     feedbackSelectionIds,
  //     type,
  //     chatOrVoiceId: this.ticket.id,
  //   });
  // }
  // handleFeedback (config: {rating: number, comment: string, feedbackSelectionIds: number[]; type: 'voice' | 'chat'}) {
  //   setTimeout(() => {
  //     this.submittedFeedback = true;
  //   }, 2000);

  //   this.postFeedback(config);
  // }

  scrollToEnd() {
    const container = this.$refs.container as HTMLDivElement;
    container.scrollTop = container.scrollHeight;
  }
  scrollToTop() {
    const container = this.$refs.container as HTMLDivElement;
    container.scrollTop = 0;
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variable.scss';

.containers {
  height: 100vh;
  width: 100%;
  position: relative;
  box-sizing: border-box;
  background-color: $my-request-detail-background;
  .title {
    font-size: $size-m2;
    @extend .text-bold;
    width: 100%;
    min-height: 3.8rem;
    padding: 1.1rem 1.6rem;
    border-bottom: $my-request-detail-subject-border-bottom;
    color: $my-request-detail-subject-text-color;
    background-color: $my-request-detail-subject-background;
  }
  .message-container {
    overflow-y: auto;
    height: calc(100% - #{$header-height} - 12rem);
    position: relative;
    &.closed {
      height: auto;
    }
    .detail {
      background-color: $my-request-detail-info-background;
      padding: 1.4rem 2rem;
      font-size: $size-xs;
      color: $my-request-detail-info-label;
      display: flex;
      line-height: 2;
      &__left {
        width: 40%;
      }
      &__right {
        width: 60%;
        @extend .text-bold;
        .status {
          border-radius: 0.2rem;
          @extend .text-regular;
          line-height: 1;
          display: inline-block;
          .active {
            color: $my-requests-item-status-active-color;
            border: $my-requests-item-status-active-color 0.1rem solid;
            padding: 0.3rem 0.8rem;
          }
          .done {
            color: $my-requests-item-status-inactive-color;
            border: $my-requests-item-status-inactive-color 0.1rem solid;
            padding: 0.3rem 0.8rem;
          }
        }
      }
    }
    .message {
      display: flex;
      background-color: $my-request-detail-comment-background;
      padding: 1.3rem 2rem 0.8rem 2rem;
      border-bottom: solid 0.05rem $my-request-detail-comment-divider-color;
      &__avatarAgent {
        width: 15%;
        img {
          height: 3rem;
          width: 3rem;
        }
      }
      &__body {
        width: 65%;
        word-wrap: break-word;
        text-align: left;
        .name {
          font-size: $size-m;
          line-height: 1.5;
          @extend .text-bold;
          color: $my-request-detail-comment-title-color;
        }
        .reply {
          font-size: $size-m2;
          line-height: 1.67;
          padding-bottom: 1.45rem;
          color: $my-request-detail-comment-body-color;
        }
        .attachment {
          &__title {
            font-size: $size_xs;
            color: $my-request-detail-comment-title-color;
          }
          &__link {
            text-decoration: underline;
            word-wrap: break-word;
            cursor: pointer;
          }
        }
      }
      &__time {
        width: 20%;
        font-size: $size-m2;
        color: $my-request-detail-comment-time-color;
        flex-wrap: nowrap;
        text-align: right;
        line-height: 1.5;
      }
      .avatar {
        width: 15%;
      }
    }
    .scroll {
      width: 2.4rem;
      position: fixed;
      bottom: 7.4rem;
      right: 2rem;
      z-index: 2;
      cursor: pointer;
      img {
        width: inherit;
      }
    }
  }

  .attachment-view {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 100;
  }
}
</style>
